// src/Utils/formatters.js

import { REF_PREFIX } from './constants';

// ============================================
// ✅ CURRENCY & NUMBER FORMATTING
// ============================================

// Format Currency (₹1,25,340)
export const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return "₹0";
  const num = parseFloat(amount);
  if (isNaN(num)) return "₹0";
  return `₹${num.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
};

// Format Number (12,568)
export const formatNumber = (num) => {
  if (num === null || num === undefined) return "0";
  return parseInt(num).toLocaleString("en-IN");
};

// ============================================
// ✅ DATE FORMATTING
// ============================================

// Format Date (31/05/2025)
export const formatDate = (date) => {
  if (!date) return "-";
  const d = new Date(date);
  if (isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

// Format Date with time
export const formatDateTime = (date) => {
  if (!date) return "-";
  const d = new Date(date);
  if (isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// ============================================
// ✅ PROFILE ID FORMATTING (BACKEND SYNC)
// ============================================

/**
 * Generate reference number based on role
 * Matches backend format: CR000001, CRUS000001, CRST000001
 * 
 * @param {string} type - 'user', 'admin', 'staff', 'superadmin'
 * @param {string|number} id - User ID or number
 * @returns {string} Formatted profile ID
 */
// src/Utils/formatters.js

export const generateRefNumber = (type = "user", id) => {
  // ✅ Priority 1: If ID already in correct backend format, return as-is
  if (id && typeof id === 'string') {
    if (
      id.startsWith('CRST') || 
      id.startsWith('CRUS') || 
      id.startsWith('COMM') ||  // ✅ ADD
      id.startsWith('TXN')  ||  // ✅ ADD
      id.startsWith('STR')  ||  // ✅ ADD
      id.startsWith('PLN')  ||  // ✅ ADD
      id.startsWith('RCH')  ||  // ✅ ADD
      id.startsWith('CR')
    ) {
      return id;
    }
  }
  
  // ✅ Priority 2: Extract number from ID
  let num;
  
  if (typeof id === 'number') {
    num = id;
  } else if (typeof id === 'string') {
    const digits = id.replace(/\D/g, '');
    num = digits ? parseInt(digits.substring(0, 6)) : 0;
  } else {
    num = 0;
  }
  
  num = Math.abs(num) % 1000000;
  const paddedNum = String(num).padStart(6, '0');
  
  switch (type?.toLowerCase()) {
    
    // ✅ USER ROLES - CRUS prefix
    case 'user':
      return `CRUS${paddedNum}`;      // CRUS000001

    // ✅ ADMIN ROLES - CR prefix  
    case 'admin':
    case 'superadmin':
      return `CR${paddedNum}`;        // CR000001

    // ✅ STAFF - CRST prefix
    case 'staff':
      return `CRST${paddedNum}`;      // CRST000001

   
    case 'commission':
    case 'postcharge':
      return `COMM${paddedNum}`;     

    // ✅ TRANSACTION - TXN prefix
    case 'transaction':
      return `TXN${paddedNum}`;       // TXN000001 ✅

    // ✅ STORY - STR prefix
    case 'story':
      return `STR${paddedNum}`;       // STR000001 ✅

    // ✅ PLAN - PLN prefix
    case 'plan':
      return `PLN${paddedNum}`;       // PLN000001 ✅

    // ✅ RECHARGE - RCH prefix
    case 'recharge':
      return `RCH${paddedNum}`;       // RCH000001 ✅

    // ✅ DEFAULT
    default:
      return `CRUS${paddedNum}`;      // CRUS000001
  }
};

/**
 * ✅ MAIN FUNCTION - Format Profile ID for display
 * Handles all cases and returns proper backend format
 * 
 * @param {Object} member - User/member object
 * @returns {string} Formatted profile ID (CR/CRUS/CRST)
 */
export const formatProfileId = (member) => {
  if (!member) return 'CRUS000000';
  
  // ✅ Priority 1: Direct profile_id from backend
  if (member.profile_id) {
    return member.profile_id;
  }
  
  // ✅ Priority 2: Nested user.profile_id
  if (member.user?.profile_id) {
    return member.user.profile_id;
  }
  
  // ✅ Priority 3: staff_id (for staff role)
  if (member.staff_id) {
    return member.staff_id;
  }
  
  // ✅ Priority 4: Check UserData nested
  if (member.UserData?.profile_id) {
    return member.UserData.profile_id;
  }
  
  // ✅ Priority 5: Generate based on role
  const role = member.role || member.user?.role || 'user';
  const id = member.id || member.user_id || member.user || 0;
  
  return generateRefNumber(role, id);
};

// ============================================
// ✅ OTHER FORMATTERS
// ============================================

// Get percentage change (+18.5%)
export const getPercentChange = (current, previous) => {
  if (!previous || previous === 0) return { value: 0, isUp: true };
  const change = ((current - previous) / previous) * 100;
  return {
    value: Math.abs(change).toFixed(1),
    isUp: change >= 0,
  };
};

// Capitalize first letter
export const capitalize = (str) => {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

// Truncate text
export const truncate = (text, length = 30) => {
  if (!text) return "";
  return text.length > length ? text.substring(0, length) + "..." : text;
};

// Get month name (Jan, Feb, etc.)
export const getMonthName = (monthNum) => {
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return months[parseInt(monthNum) - 1] || "";
};

// Serial Number (S.No.)
export const getSerialNumber = (index, page = 1, pageSize = 10) => {
  return (page - 1) * pageSize + index + 1;
};

// ============================================
// ✅ BONUS - Helper Functions
// ============================================

/**
 * Get user role display name
 */
export const getRoleDisplay = (role) => {
  const roleMap = {
    'user': '👤 User',
    'admin': '👑 Admin',
    'superadmin': '⭐ Super Admin',
    'staff': '👨‍💼 Staff',
    'developer': '💻 Developer'
  };
  return roleMap[role?.toLowerCase()] || role || 'User';
};

/**
 * Get profile ID color based on role
 */
export const getProfileIdColor = (member) => {
  const role = member?.role || member?.user?.role || 'user';
  
  const colorMap = {
    'user': '#ff0055',        // Pink
    'admin': '#722ed1',       // Purple
    'superadmin': '#eb2f96',  // Magenta
    'staff': '#1890ff',       // Blue
    'developer': '#52c41a'    // Green
  };
  
  return colorMap[role?.toLowerCase()] || '#ff0055';
};